/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData } from "@cosmos/hygraph-data";
import { EntityTypeName, Locale } from "@cosmos/hygraph-schema";

import { getExternalSectionData } from "./external";

import type { SectionTypeName } from "./page.types";
import type { ContentTypeNames } from "@cosmos/hygraph-data";
import type {
	EntityWhereInput,
	PageSectionsFragment,
	RefSectionsEntitiesFragment,
	RefSectionsFragment,
} from "@cosmos/hygraph-schema";

/**
 * Shape of a per-section-type ref query response: `entities` holds the resolved
 * reference section, whose `section` is the single inner section the per-type
 * query expanded.
 */
type RefSectionEntitiesResponse = {
	entities?: Array<{ id?: string | null; section?: PageSectionsFragment | null } | null | undefined>;
} | void;

/**
 * Map an inner section `__typename` (e.g. `CosmosSectionAsset`) to its per-type
 * ref-section query name (`CosmosPageRefSectionAsset`) — the query that resolves a
 * single `CosmosReferenceSection` by id and expands only that one inner section
 * type, keeping query complexity low versus inlining the whole `PageSections` union.
 */
export const getSectionEntityContentType = (typename: EntityTypeName): ContentTypeNames => {
	return typename.replace(/^Cosmos/, "CosmosPageRef") as ContentTypeNames;
};

/**
 * Compile-time guards for the per-type dispatch above. Every resolvable section
 * type must (a) exist as an `EntityTypeName` and (b) have a matching per-type
 * `CosmosPageRefSection<Type>` query in the SDK. If a section is added to the
 * `PageSections` union without the enum entry or the query in
 * `page-sections.query.gql`, one of these fails to type-check — surfacing the gap
 * at build time instead of silently dropping / erroring at runtime.
 */
type SectionQueryName = SectionTypeName extends `Cosmos${infer Rest}` ? `CosmosPageRef${Rest}` : never;
type UnmappedSectionTypes = Exclude<SectionTypeName, keyof typeof EntityTypeName>;
type MissingSectionQueries = Exclude<SectionQueryName, ContentTypeNames>;
const _assertSectionTypesAreEntities: [UnmappedSectionTypes] extends [never] ? true : UnmappedSectionTypes = true;
const _assertSectionQueriesExist: [MissingSectionQueries] extends [never] ? true : MissingSectionQueries = true;
void _assertSectionTypesAreEntities;
void _assertSectionQueriesExist;

/**
 * Fetch each reference section's inner section by dispatching a per-section-type
 * query. The query is keyed by the `CosmosReferenceSection`'s own `id`/`stage` (so
 * Hygraph resolves the section through the ref-section relation), but the query is
 * chosen from the inner section's `__typename` so only that one section type is
 * expanded — avoiding the high complexity of resolving the full `PageSections`
 * union. External data is resolved where needed and results are shaped as
 * `RefSectionsFragment` so the rest of the render pipeline is unchanged.
 */
export const getSectionEntityQueries = (
	refSectionsEntities: RefSectionsEntitiesFragment[],
	locale: string,
	lang: string,
	preview?: string,
): Promise<null | RefSectionsFragment>[] => {
	return refSectionsEntities.map((refSection) => {
		const sectionStub = refSection.section;
		if (!sectionStub?.__typename) return Promise.resolve(null);

		// Guard against a section `__typename` with no `EntityTypeName` mapping (e.g. a
		// section type live in Hygraph but ahead of the generated types): skip it with a
		// clear warning rather than throwing downstream and dropping it silently.
		const entityTypeName = EntityTypeName[sectionStub.__typename];
		if (!entityTypeName) {
			console.warn("Section skipped: section __typename has no EntityTypeName mapping", {
				typename: sectionStub.__typename,
			});
			return Promise.resolve(null);
		}

		// Filter on the reference section itself, not the inner section.
		const entity: EntityWhereInput = {
			id: refSection.id,
			stage: refSection.stage,
			typename: EntityTypeName.CosmosReferenceSection,
		};
		// ...but pick the per-type query from the inner section's type so the query expands
		// only this one section, keeping complexity low.
		const contentType = getSectionEntityContentType(entityTypeName);

		return getHygraphCosmosPageData(
			contentType,
			{ entities: [entity], locale: [locale, Locale.En] as Locale[] },
			preview,
		)
			.then((response) => {
				const section = (response as RefSectionEntitiesResponse)?.entities?.[0]?.section;
				if (!section?.__typename) return null;
				const refSectionFragment: RefSectionsFragment = {
					__typename: "CosmosReferenceSection",
					id: refSection.id,
					section,
				};
				return getExternalSectionData(section.__typename, lang, refSectionFragment);
			})
			.catch((e) => {
				console.error("Section load error", { entity, error: e });
				return null;
			});
	});
};

/**
 * Fetch the sections for the reference-section stubs.
 */
export const getRefSectionEntities = (
	refSectionsEntities: RefSectionsEntitiesFragment[] | undefined | null,
	locale: string,
	lang: string,
	preview?: string,
): Promise<null | RefSectionsFragment>[] => {
	const withSection = (refSectionsEntities || []).filter((refSection) => refSection.section?.__typename);
	if (!withSection.length) {
		return [];
	}
	return getSectionEntityQueries(withSection, locale, lang, preview);
};

export const getRefSectionDetails = (initResponses: Array<null | RefSectionsFragment>): RefSectionsFragment[] => {
	return (initResponses || []).filter((element): element is RefSectionsFragment => !!element).flat();
};
